import useIntersectionObserver from 'hooks/common/useIntersectionObserver';
import {
  clearCache,
  fetchPage,
  markAllRead,
  readCache,
  readUnreadBaseline,
  storeCache,
  type ListCache,
} from 'pages/notification/notificationService';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NOTIFICATION_TABS, type Notification, type NotificationTab } from 'types/notification';

/** 무한 스크롤이 다음 장을 당기는 거리. vanilla Scroller 가 쓰던 값 그대로다. */
const LOAD_AHEAD = '200px';

type TabState = {
  items: Notification[];
  token: unknown;
  done: boolean;
  loading: boolean;
  /** 한 번이라도 받아 봤는지. 빈 목록과 아직 안 받은 상태를 가른다. */
  loaded: boolean;
};

const EMPTY: TabState = { items: [], token: null, done: false, loading: false, loaded: false };

type TabMap = Record<NotificationTab, TabState>;

function initialState(restored: ListCache | null): TabMap {
  const next = {} as TabMap;
  NOTIFICATION_TABS.forEach((tab) => {
    const cached = restored?.tabs[tab];
    next[tab] = cached ? { ...cached, loading: false, loaded: true } : { ...EMPTY };
  });
  return next;
}

const useNotificationList = (listRef: React.RefObject<HTMLDivElement>) => {
  const location = useLocation();
  const [restored] = useState(() => readCache(location.key));

  // 기준선은 화면에 들어온 순간 한 번만 정한다. 스크롤 도중에 바뀌면 밑줄이 따라 움직인다.
  const baseTsRef = useRef(readUnreadBaseline());
  const [tab, setTab] = useState<NotificationTab>(restored?.tab ?? 'all');
  const [state, setState] = useState<TabMap>(() => initialState(restored));
  const [refreshing, setRefreshing] = useState(false);

  // 리셋 중에 늦게 도착한 응답을 버리기 위한 표식.
  const reqIdRef = useRef(0);
  const markedRef = useRef(false);

  // 언마운트 시점에 최신 값을 담기 위한 거울.
  const stateRef = useRef(state);
  stateRef.current = state;
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const load = useCallback(async (target: NotificationTab, token: unknown) => {
    const reqId = ++reqIdRef.current;
    setState((prev) => ({ ...prev, [target]: { ...prev[target], loading: true } }));

    const page = await fetchPage(target, token, baseTsRef.current);
    if (reqId !== reqIdRef.current) return;

    setState((prev) => ({
      ...prev,
      [target]: {
        items: [...prev[target].items, ...page.items],
        token: page.token,
        done: page.done,
        loading: false,
        loaded: true,
      },
    }));

    // 한 건이라도 그려졌으면 이 화면의 알림을 다 본 것으로 친다.
    if (!markedRef.current && page.items.length > 0) {
      markedRef.current = true;
      markAllRead();
    }
  }, []);

  // 고른 탭이 아직 안 받았으면 첫 장을 받는다.
  useEffect(() => {
    const current = state[tab];
    if (current.loaded || current.loading) return;
    load(tab, null);
  }, [tab, state, load]);

  const loadMore = useCallback(() => {
    const current = stateRef.current[tabRef.current];
    if (current.loading || current.done) return;
    load(tabRef.current, current.token);
  }, [load]);

  // 관찰자는 보이는 탭에만 붙인다 — 나머지 탭은 아예 돌지 않는다.
  const current = state[tab];
  const observerRef = useIntersectionObserver({
    onIntersect: loadMore,
    enabled: current.loaded && !current.done && !current.loading,
    rootMargin: LOAD_AHEAD,
  });

  const refresh = useCallback(() => {
    const target = tabRef.current;
    reqIdRef.current++;
    setRefreshing(true);
    clearCache();
    setState((prev) => ({ ...prev, [target]: { ...EMPTY } }));
    load(target, null).finally(() => setRefreshing(false));
  }, [load]);

  // 상점·프로필에 다녀와도 목록과 스크롤이 그대로이도록 담아 둔다.
  useEffect(() => {
    const key = location.key;
    const ref = listRef;
    return () => {
      const tabs: ListCache['tabs'] = {};
      NOTIFICATION_TABS.forEach((name) => {
        const { items, token, done } = stateRef.current[name];
        if (items.length > 0) tabs[name] = { items, token, done };
      });
      storeCache({ key, tabs, tab: tabRef.current, scrollTop: ref.current?.scrollTop ?? 0 });
    };
  }, [location.key, listRef]);

  return {
    tab,
    setTab,
    items: current.items,
    loading: current.loading,
    loaded: current.loaded,
    refreshing,
    observerRef,
    refresh,
    restoredScrollTop: restored?.scrollTop ?? 0,
  };
};

export default useNotificationList;
